import { z } from 'zod';

import {
  isoDateTimeSchema,
  nonNegativeIntSchema,
  positiveIntSchema,
  semverSchema,
  uuidSchema,
} from '../common/primitives';

import { nodeCapacitySchema } from './capacity';
import { nodeInventorySchema } from './inventory';
import { nodeMetricsSchema } from './metrics';
import { preflightReportSchema } from './preflight';

/**
 * Der Heartbeat — die einzige Quelle, aus der der Master seinen Ist-Zustand bezieht.
 *
 * Der Agent sendet alle 10 Sekunden. Der Master leitet daraus ab: Ist der Node
 * erreichbar, wie ausgelastet ist er, welche Sites laufen dort tatsächlich.
 * Kein anderer Mechanismus darf `observedState` schreiben.
 */

export const heartbeatRequestSchema = z.object({
  /**
   * Generation aus dem Enrollment. Erhöht sich bei jedem Neustart des Agents.
   *
   * Das ist der Neustart-Detektor: Ändert sich die Generation, weiß der Master,
   * dass der Agent seinen Speicher verloren hat, und erzwingt eine vollständige
   * Resynchronisierung, statt auf inkrementelle Annahmen zu vertrauen.
   */
  generation: positiveIntSchema,
  agentVersion: semverSchema,
  /** Laufzeit des Agent-Prozesses in Sekunden (nicht des Hosts). */
  agentUptimeSeconds: nonNegativeIntSchema,
  /** Zeitpunkt der Messung auf dem Worker — deckt Uhren-Drift auf. */
  sampledAt: isoDateTimeSchema,
  metrics: nodeMetricsSchema,
  /**
   * Vollständiges Ist-Inventar, niemals ein Delta. Siehe `inventory.ts`.
   *
   * `null` heißt ausdrücklich **"nicht ermittelbar"** — etwa weil der
   * Docker-Daemon nicht antwortet. Das ist etwas grundlegend anderes als `[]`
   * ("nachweislich keine Sites vorhanden"), und die Unterscheidung ist nicht
   * kosmetisch: Läse der Master ein nicht ermittelbares Inventar als leeres,
   * hielte er sämtliche Sites des Nodes für verschwunden und würde eine ganze
   * Maschine gleichzeitig neu provisionieren. Ein `null` an dieser Stelle
   * bedeutet für den Reconciler `unknown`, und `unknown` heißt: nichts tun.
   */
  inventory: nodeInventorySchema.nullable(),
  /** IDs der Operationen, die der Agent gerade ausführt. */
  activeOperationIds: z.array(uuidSchema).max(100).default([]),
  /**
   * Aktueller Preflight-Bericht.
   * Wird nur bei Statusänderung oder alle paar Minuten neu erhoben; der Agent
   * schickt immer den letzten bekannten Stand mit, damit der Master keine
   * Extra-Abfrage braucht.
   */
  preflight: preflightReportSchema,
  /**
   * Ausstattung des Nodes. Wird mitgeschickt, weil sich Server vergrößern
   * lassen — der Master aktualisiert die Kapazität dann automatisch, statt auf
   * eine manuelle Pflege zu warten.
   */
  capacity: nodeCapacitySchema,
});

export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

/**
 * Anweisungen, die der Master dem Agent huckepack auf die Heartbeat-Antwort gibt.
 *
 * Damit braucht der Master keinen Rückkanal für einfache Steuerbefehle: Statt
 * einer offenen Verbindung genügt die Antwort auf den nächsten Heartbeat, der
 * ohnehin fließt. Das hält den Worker frei von eingehenden Verbindungen für
 * Steuerungszwecke.
 */
export const heartbeatDirectivesSchema = z.object({
  /**
   * Der Master hat einen Generationswechsel oder eine Abweichung erkannt und
   * fordert einen vollständigen Neuabgleich des Inventars an.
   */
  resyncRequested: z.boolean().default(false),
  /** Angepasstes Sendeintervall — erlaubt Lastdrosselung bei großen Flotten. */
  heartbeatIntervalMs: positiveIntSchema,
  /**
   * Der Agent soll seine Credentials erneuern (Rotation steht an oder der
   * Master hat den Node kompromittiert markiert).
   */
  rotateCredentials: z.boolean().default(false),
  /**
   * Der Node ist administrativ stillgelegt. Der Agent nimmt keine neuen
   * Operationen an, hält laufende Sites aber weiter am Leben.
   */
  drainRequested: z.boolean().default(false),
});

export type HeartbeatDirectives = z.infer<typeof heartbeatDirectivesSchema>;

export const heartbeatResponseSchema = z.object({
  /** Serverzeit des Masters — der Agent kann damit Uhren-Drift melden. */
  receivedAt: isoDateTimeSchema,
  /** Vom Master gesetzter Status, wie er in der Flotten-Übersicht erscheint. */
  acknowledgedGeneration: positiveIntSchema,
  directives: heartbeatDirectivesSchema,
});

export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;

/**
 * Maximal tolerierte Abweichung zwischen Worker- und Master-Uhr.
 *
 * Größere Drift wird geloggt und als Warnung am Node vermerkt. Sie ist kein
 * Ausschlusskriterium, aber eine häufige und schwer zu findende Fehlerursache:
 * Zeitstempel-basierte Ablauflogik (Token, Zertifikate, Backup-Fenster) bricht
 * dabei auf eine Weise, die im Log wie ein Zufallsfehler aussieht.
 */
export const MAX_CLOCK_DRIFT_MS = 30_000;
